import { Box, Grid, Stack, Typography } from '@mui/material'
import { Card, CustomIconName, EmptyState, Icon, IconDictionary, theme, Spinner } from '@ketch-com/deck'
import { RequestStatusBreakdownChart } from '../components/RequestStatusBreakdownChart'
import { RightStatsPeriodDTO } from 'interfaces/rights/rightStats'

const requestStatusLegend = [
  { name: 'On Track', color: theme.palette.Action.Primary },
  { name: 'Due Today', color: theme.palette.Error.PrimaryLight },
  { name: 'Due Soon', color: theme.palette.Warning.Primary },
  { name: 'Overdue', color: theme.palette.Error.PrimaryDark },
]

enum RequestStatusMetricKey {
  AvgOnTrack = 'avgOnTrack',
  AvgDueSoon = 'avgDueSoon',
  AvgUrgent = 'avgUrgent',
  AvgPending = 'avgPending',
}

const queueSummaryCards: {
  label: string
  key: RequestStatusMetricKey
  icon: CustomIconName
}[] = [
  {
    label: 'On Track',
    key: RequestStatusMetricKey.AvgOnTrack,
    icon: IconDictionary.FPointer,
  },
  {
    label: 'Due Today',
    key: RequestStatusMetricKey.AvgDueSoon,
    icon: IconDictionary.OClock,
  },
  {
    label: 'Due Soon',
    key: RequestStatusMetricKey.AvgUrgent,
    icon: IconDictionary.OClock,
  },
  {
    label: 'Overdue',
    key: RequestStatusMetricKey.AvgPending,
    icon: IconDictionary.FClock,
  },
]

type Props = {
  data: RightStatsPeriodDTO | null | undefined
  isLoading: boolean
}

export const StatusBreakdownSection = ({ data, isLoading }: Props) => {
  const requestStatusData =
    data?.dataPoints?.map(dp => ({
      date: dp.date ?? '',
      onTrack: dp.onTrack ?? 0,
      dueToday: dp.dueOneDay ?? 0,
      dueSoon: dp.dueFourteenDays ?? 0,
      overdue: dp.overdue ?? 0,
    })) ?? []

  return (
    <Stack direction="row" spacing={3}>
      <Stack sx={{ flex: 3, mt: 1 }} spacing={2}>
        <Typography variant="h4">Requests Status Breakdown</Typography>
        {isLoading && requestStatusData.length === 0 ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height={300}
            border={`1px solid ${theme.palette.Black.o16}`}
            borderRadius="11px"
          >
            <Spinner size="32px" thickness={2} />
          </Box>
        ) : requestStatusData.length === 0 ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            border={`1px solid ${theme.palette.Black.o16}`}
            borderRadius="11px"
            height={300}
          >
            <EmptyState
              size="small"
              colorTheme={theme.palette.Text.Secondary}
              iconName={IconDictionary.OChart}
              title="No Data Yet"
              subTitle="Adjust filters or wait for data to become available."
            />
          </Box>
        ) : (
          <RequestStatusBreakdownChart data={requestStatusData} />
        )}
      </Stack>
      <Grid container spacing={2} sx={{ flex: 2 }}>
        <Grid item xs={12}>
          <Stack direction="row" spacing={4} alignItems="center">
            {requestStatusLegend.map(({ name, color }) => (
              <Stack key={name} direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="smallBody">{name}</Typography>
              </Stack>
            ))}
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="label">View Average</Typography>
        </Grid>
        {queueSummaryCards.map(({ label, key, icon }) => (
          <Grid item xs={6} key={key}>
            <Card
              cardVariant="stretchWidth"
              cardTitle={`${data?.[key] ?? 0}`}
              subTitle={label}
              primaryIcon={
                <Icon name={IconDictionary[icon]} width="32px" height="32px" iconColor={theme.palette.Black.o48} />
              }
              isNumerical
              sx={{
                borderRadius: '16px',
                border: '1px solid rgba(7, 26, 36, 0.16)',
              }}
            />
          </Grid>
        ))}
      </Grid>
    </Stack>
  )
}
